import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../api";
import PublicNavbar from "../components/PublicNavbar";
import { homeFor } from "../auth";
import { registerPushNotifications } from "../pushNotifications";
import { Mail, Lock, Phone, User, AlertCircle, CheckCircle } from "lucide-react";

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    workshopName: "",
    abn: "",
    jobTitle: "",
    role: "user"
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  // Check password strength
  const checkPasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    setPasswordStrength(strength);
  };

  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (field === "password") {
      checkPasswordStrength(value);
    }
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      setError("Please enter your full name");
      return false;
    }
    if (!form.email.trim()) {
      setError("Please enter your email");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!form.password) {
      setError("Please enter a password");
      return false;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        mobile: form.mobile || undefined,
        password: form.password,
        workshopName: form.workshopName || undefined,
        abn: form.abn || undefined,
        jobTitle: form.jobTitle || undefined,
        role: form.role
      });

      if (!data?.token || !data?.user) {
        throw new Error("Registration succeeded but no login session was returned.");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setSuccess(true);

      await registerPushNotifications({ requestPermission: true });
      navigate(homeFor(data.user), { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return "bg-gray-200";
    if (passwordStrength === 1) return "bg-red-500";
    if (passwordStrength === 2) return "bg-orange-500";
    if (passwordStrength === 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <PublicNavbar />
      <div className="max-w-md mx-auto px-4 pt-8 pb-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="font-extrabold text-3xl text-slate-800 mb-2">
            FIND <span className="text-red-600">AUTO</span> PARTS
          </div>
          <p className="text-slate-500 text-sm">Create your account to get started</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
            <div>
              <p className="text-green-800 font-semibold text-sm">Registration successful!</p>
              <p className="text-green-700 text-xs mt-1">Signing you in...</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <User size={16} className="inline mr-2" />
              Full Name
            </label>
            <input
              type="text"
              placeholder="John Doe"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              value={form.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Mail size={16} className="inline mr-2" />
              Email Address
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              value={form.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Phone size={16} className="inline mr-2" />
              Mobile Number (Optional)
            </label>
            <input
              type="tel"
              placeholder="+61 400 000 000"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              value={form.mobile}
              onChange={(e) => handleInputChange("mobile", e.target.value)}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Lock size={16} className="inline mr-2" />
              Password
            </label>
            <div className="relative"><input
              type={showPassword ? "text" : "password"}
              placeholder="Enter a strong password"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              value={form.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              required
            /><button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-3 text-xs font-semibold text-blue-700" aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? "Hide" : "Show"}</button></div>
            {form.password && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition ${i < passwordStrength ? getPasswordStrengthColor() : "bg-gray-200"
                        }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  {passwordStrength === 0 && "Very weak"}
                  {passwordStrength === 1 && "Weak"}
                  {passwordStrength === 2 && "Fair"}
                  {passwordStrength === 3 && "Good"}
                  {passwordStrength === 4 && "Strong"}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Lock size={16} className="inline mr-2" />
              Confirm Password
            </label>
            <div className="relative"><input
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm your password"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              value={form.confirmPassword}
              onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
              required
            /><button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-3 text-xs font-semibold text-blue-700" aria-label={showConfirm ? "Hide confirmation password" : "Show confirmation password"}>{showConfirm ? "Hide" : "Show"}</button></div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || success}
            className="w-full py-3 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {loading ? "Creating Account..." : success ? "Success!" : "Sign Up"}
          </button>

          {/* Login Link */}
          <p className="text-center text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-green-600 font-semibold hover:text-green-700 transition">
              Login here
            </Link>
          </p>
        </form>

        {/* Optional Fields Section (Collapsible) */}
        <details className="mt-6 bg-white rounded-lg shadow p-4">
          <summary className="font-semibold text-slate-700 cursor-pointer hover:text-slate-900">
            Additional Information (Optional)
          </summary>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Workshop Name
              </label>
              <input
                type="text"
                placeholder="Your workshop name"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.workshopName}
                onChange={(e) => handleInputChange("workshopName", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                ABN (Australian Business Number)
              </label>
              <input
                type="text"
                placeholder="Your ABN"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.abn}
                onChange={(e) => handleInputChange("abn", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Job Title
              </label>
              <input
                type="text"
                placeholder="Your job title"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.jobTitle}
                onChange={(e) => handleInputChange("jobTitle", e.target.value)}
              />
            </div>
          </div>
        </details>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-8">
          By signing up, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
